import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  Menu, X, User, LogOut, LayoutDashboard, Search, Phone, ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const { user, logout, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Fetch profile photo for students
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      if (isAuthenticated && user?.role === 'student') {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/profile/student`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.photo_url) {
            setProfilePhoto(res.data.photo_url);
          }
        } catch (e) {
          // Ignore errors
        }
      }
    };
    fetchProfilePhoto();
  }, [isAuthenticated, user]);

  // Scroll effect for sticky navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const getDashboardLink = () => {
    if (!user) return '/auth/login';
    switch (user.role) {
      case 'student': return '/student/dashboard';
      case 'employer': return '/employer/dashboard';
      case 'admin': return '/admin/dashboard';
      default: return '/auth/login';
    }
  };

  // Simple navigation links without dropdowns
  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Programs', href: '/programs' },
    { label: 'Courses', href: '/courses' },
    { label: 'Partner', href: '/franchise' },
    { label: 'Job Portal', href: '/jobs' },
    { label: 'Contact', href: '/contact' }
  ];

  // Check if current page
  const isActive = (href) => location.pathname === href;

  return (
    <>
      <header 
        data-testid="main-header" 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled 
            ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-slate-100" 
            : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/h002c2co_1%20copy.png" 
                alt="Iqra Skills Logo" 
                className="h-10 lg:h-12 w-auto"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/rs72rv6h_Untitled-1%201.png" 
                alt="Skill India Logo" 
                className="h-8 lg:h-10 w-auto hidden sm:block"
              />
            </Link>

            {/* Desktop Navigation - Modern Pill Style */}
            <nav className="hidden lg:flex items-center gap-3">
              {navLinks.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.href}
                  className={cn(
                    "px-5 py-2.5 rounded-[30px] font-semibold text-sm transition-all duration-300 ease-in-out",
                    "font-['Poppins',sans-serif]",
                    isActive(item.href) 
                      ? "bg-[#0A4DFF] text-white shadow-lg shadow-blue-500/30" 
                      : "text-slate-700 hover:bg-[#0A4DFF] hover:text-white hover:shadow-lg hover:shadow-blue-500/20"
                  )}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right Side - Search & Auth */}
            <div className="flex items-center gap-3">
              {/* Search Icon */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className={cn(
                  "p-2 rounded-xl transition-colors hidden md:flex",
                  scrolled ? "hover:bg-slate-100" : "hover:bg-white/20"
                )}
                data-testid="search-toggle"
              >
                <Search className={cn("w-5 h-5", scrolled ? "text-slate-600" : "text-slate-700")} />
              </button>

              {/* Auth Buttons - Desktop */}
              <div className="hidden lg:flex items-center gap-2">
                {isAuthenticated ? (
                  <div className="relative group">
                    <button
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden">
                        {profilePhoto ? (
                          <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white text-sm font-bold">
                            {user?.name?.charAt(0) || 'U'}
                          </span>
                        )}
                      </div>
                      <span className="font-medium text-slate-700">{user?.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </button>

                    {/* Profile Dropdown */}
                    <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[200px]">
                        <div className="px-4 py-2 border-b border-slate-100">
                          <p className="font-medium text-slate-900">{user?.name}</p>
                          <p className="text-xs text-slate-500">{user?.email}</p>
                        </div>
                        <Link
                          to={getDashboardLink()}
                          className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                          data-testid="nav-dashboard"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                          data-testid="logout-button"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button 
                      variant="ghost" 
                      onClick={() => navigate('/auth/login')} 
                      className={cn(
                        "font-medium rounded-xl",
                        scrolled 
                          ? "text-slate-700 hover:text-blue-600 hover:bg-slate-100" 
                          : "text-slate-700 hover:text-blue-600 hover:bg-white/20"
                      )}
                      data-testid="login-button"
                    >
                      Login
                    </Button>
                    <Button 
                      onClick={() => navigate('/auth/register')} 
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl px-5 shadow-lg shadow-blue-500/20"
                      data-testid="register-button"
                    >
                      Register
                    </Button>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-slate-700" />
                ) : (
                  <Menu className="h-6 w-6 text-slate-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar - Expanded */}
        {showSearch && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-slate-100 p-4">
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search courses, programs, jobs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Navigation */}
        <div className={cn(
          "lg:hidden fixed inset-0 top-16 bg-white transition-transform duration-300 overflow-y-auto",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}>
          <div className="p-4">
            {/* Mobile Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Mobile Menu Links - Pill Style */}
            <div className="flex flex-wrap gap-2">
              {navLinks.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "px-5 py-2.5 rounded-[30px] font-semibold text-sm transition-all duration-300 ease-in-out",
                    "font-['Poppins',sans-serif]",
                    isActive(item.href) 
                      ? "bg-[#0A4DFF] text-white shadow-lg shadow-blue-500/30" 
                      : "text-slate-700 bg-slate-100 hover:bg-[#0A4DFF] hover:text-white"
                  )}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Mobile Auth Buttons */}
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden">
                      {profilePhoto ? (
                        <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold">
                          {user?.name?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white rounded-xl font-medium"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Go to Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 w-full py-3 border border-red-200 text-red-600 rounded-xl font-medium"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={() => { navigate('/auth/login'); setMobileMenuOpen(false); }}
                    variant="outline"
                    className="w-full py-3 rounded-xl font-medium"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => { navigate('/auth/register'); setMobileMenuOpen(false); }}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-xl font-medium"
                  >
                    Register Now
                  </Button>
                </>
              )}

              {/* Contact Info */}
              <a 
                href="tel:+919521484003"
                className="flex items-center justify-center gap-2 w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-medium"
              >
                <Phone className="w-4 h-4" />
                Call: 9521484003
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-20" />
    </>
  );
};

export default Header;
